/**
 * 安聆 SafeListen - Hash 生成与校验工具
 * 动物列表用英文 key，展示时按当前语言翻译（animals.xxx）
 * 字符集排除易混淆：0/O、1/I/L
 */

/** 动物英文 key，与 i18n animals.xxx 对应 */
export const ANIMAL_KEYS = [
  'panda', 'cat', 'dog', 'rabbit', 'bear', 'deer', 'fox', 'squirrel',
  'whale', 'dolphin', 'koala', 'penguin', 'hippo', 'sheep', 'monkey',
  'swan', 'swallow', 'seagull', 'peacock', 'lion', 'elephant', 'hedgehog', 'raccoon'
];

/** 兼容旧配对码：中文动物名 -> key */
const ANIMAL_ZH_TO_KEY = {
  '熊猫': 'panda', '猫咪': 'cat', '狗狗': 'dog', '兔子': 'rabbit', '小熊': 'bear',
  '小鹿': 'deer', '狐狸': 'fox', '松鼠': 'squirrel', '鲸鱼': 'whale', '海豚': 'dolphin',
  '考拉': 'koala', '企鹅': 'penguin', '河马': 'hippo', '绵羊': 'sheep', '猴子': 'monkey',
  '天鹅': 'swan', '燕子': 'swallow', '海鸥': 'seagull', '孔雀': 'peacock', '狮子': 'lion',
  '小象': 'elephant', '刺猬': 'hedgehog', '浣熊': 'raccoon'
};

/** Hash 字符集：0-9 + 大写字母，排除 I/L/O 易混淆 */
const HASH_CHARS = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/**
 * 生成 6 位随机 Hash
 * @returns {string} 6 位字符串
 */
export function generateRandomHash() {
  let hash = '';
  const len = HASH_CHARS.length;
  for (let i = 0; i < 6; i++) {
    hash += HASH_CHARS.charAt(Math.floor(Math.random() * len));
  }
  return hash;
}

/**
 * 从动物 key 随机选一，生成「key-6位Hash」配对码（存储用，展示用 getPairingCodeDisplay）
 * @param {string} [hash] 可选，不传则内部生成
 * @returns {string} 如 "bear-3GQ2Y8"
 */
export function buildPairingCode(hash) {
  const animalKey = ANIMAL_KEYS[Math.floor(Math.random() * ANIMAL_KEYS.length)];
  const code = (hash || generateRandomHash()).toUpperCase().slice(0, 6);
  return `${animalKey}-${code}`;
}

/**
 * 按当前语言展示配对码（动物部分用 t('animals.xxx')）
 * @param {string} code 如 "squirrel-MNRA2Y" 或 "松鼠-MNRA2Y"
 * @param {function} t i18n t
 * @returns {string} 如 "Ardilla-MNRA2Y"（西语）或 "松鼠-MNRA2Y"（中文）
 */
export function getPairingCodeDisplay(code, t) {
  if (!code || !t) return code || '';
  const parts = String(code).trim().split('-');
  const hashPart = parts[1] ? parts[1].toUpperCase().slice(0, 6) : '';
  let animalPart = (parts[0] || '').trim();
  if (!hashPart || hashPart.length !== 6) return code;
  const key = ANIMAL_ZH_TO_KEY[animalPart] || (ANIMAL_KEYS.includes(animalPart) ? animalPart : null);
  const label = key ? (t(`animals.${key}`) || animalPart) : animalPart;
  return `${label}-${hashPart}`;
}

/**
 * 校验配对码格式：动物名(2~8字) + - + 6位字符（0-9/A-Z 排除 I,L,O）
 * @param {string} input 用户输入
 * @returns {boolean}
 */
export function validatePairingCodeFormat(input) {
  if (!input || typeof input !== 'string') return false;
  const normalized = input.replace(/\s/g, '');
  const re = /^[\u4e00-\u9fa5a-zA-Z]{2,8}-[0-9A-Z]{6}$/;
  return re.test(normalized);
}

/**
 * 提交时规范化配对码：若用户未输入中间的「-」，则按「前若干字+6位码」自动补上，便于校验
 * 仅用于提交校验，不在输入时自动插入
 * @param {string} value 用户输入（可能无 -）
 * @returns {string} 如 "小熊3GQ2Y8" -> "小熊-3GQ2Y8"
 */
export function normalizePairingInput(value) {
  if (!value || typeof value !== 'string') return value;
  const s = value.replace(/\s/g, '').replace(/-/g, '');
  if (s.length >= 8 && !value.includes('-')) {
    const tail = s.slice(-6);
    const head = s.slice(0, s.length - 6);
    if (/^[0-9A-Z]{6}$/.test(tail)) return head + '-' + tail;
  }
  return value.replace(/\s/g, '');
}
